import { yupResolver } from "@hookform/resolvers/yup";
import React, { KeyboardEventHandler, useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import ReactHtmlParser from "react-html-parser";
import { useTranslation } from "react-i18next";
import { BiX } from "react-icons/bi";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";

import { useAppSelector } from "../../../../app/hook";
import { AlertsType } from "../../../../constants/alerts.constant";
import { selectUseId } from "../../../../features/auth/auth.slice";
import { BlockType } from "../../../../interfaces/models/Knowledge/scenario.interface";
import HintBlock from "../../../common/HintBlock";
import AddNoteButton from "../AddNoteButton";
import DeleteButton from "../DeleteButton";
import NodeParentChanger from "../NodeParentChanger";
import NoteForm, { getNoteSchema, INoteForm } from "../NoteForm";
import ScenarioHeader from "../ScenarioHeader";
import { MacBotActionKind } from "../types";
import ScenarioSide from "./scenario-side";

interface IUserInputForm extends INoteForm {
  BlockTitle: string;
  Keywords: string[];
  isCaseSensitive: boolean;
}

export const MacBotUserInputForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  tree: any[];
}) => {
  const { t } = useTranslation();

  const [inputValue, setInputValue] = useState("");
  const userId = useAppSelector(selectUseId);
  const schema = Yup.object({
    Keywords: Yup.array().min(1, t("MacBotUserInputForm/er-01")),
    isCaseSensitive: Yup.boolean(),
    ...getNoteSchema(t)
  });

  const {
    register,
    formState: { errors },
    reset,
    watch,
    trigger,
    getValues,
    setValue,
    handleSubmit,
    control
  } = useForm<IUserInputForm>({
    defaultValues: {
      BlockTitle: itemData?.title ?? "",
      Keywords: itemData?.keywords ?? [],
      isCaseSensitive: itemData?.isCaseSensitive ?? false,
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: "",
          description: "",
          qc: false,
          author: userId
        }
      ]
    },
    resetOptions: {
      keepDefaultValues: true
    },
    resolver: yupResolver(schema) as any,
    mode: "onSubmit"
  });
  const { fields } = useFieldArray({
    control,
    name: "notes"
  });

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const handleManageKeywords: KeyboardEventHandler = (event) => {
    if (!inputValue) return;

    switch (event.key) {
      case "Enter":
        event.preventDefault();

        if (watch("Keywords")?.some((item: string) => item == inputValue)) {
          toast.error(t(AlertsType.ISKBKO027));
          break;
        }

        setValue("Keywords", [...watch("Keywords"), inputValue]);

        setInputValue("");

        break;
      case "Tab":
        if (watch("Keywords")?.some((item: string) => item == inputValue)) {
          toast.error(t(AlertsType.ISKBKO027));
          break;
        }

        setValue("Keywords", [...watch("Keywords"), inputValue]);

        setInputValue("");

        break;
      default:
        break;
    }
  };

  const ButtonSubmit = (data: any) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.UserInput,
        title: data.Keywords[0],
        parent: itemData.parent,
        keywords: data.Keywords,
        isCaseSensitive: data.isCaseSensitive,
        notes: data.notes
      };
      changeScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    } else {
      const _data = {
        type: BlockType.UserInput,
        title: data.Keywords[0],
        parent: parentId,
        keywords: data.Keywords,
        isCaseSensitive: data.isCaseSensitive,
        notes: data.notes
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    }
  };

  useEffect(() => {
    reset({
      BlockTitle: itemData?.title ?? "",
      Keywords: itemData?.keywords ?? [],
      isCaseSensitive: itemData?.isCaseSensitive ?? false,
      notes: itemData?.notes
    });
  }, [itemData]);

  return (
    <React.Fragment>
      <ScenarioSide size="md" onSubmit={handleSubmit(ButtonSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowUserInputForm}
          onClose={() => {
            reset();
            setShow?.(false);
          }}
        />

        <div className="tw-mt-5 tw-p-4">
          <Row className="gap-3">
            <Col lg="12">
              <Form.Group>
                <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {ReactHtmlParser(t("MacBotUserInputForm/l-03"))}
                </Form.Label>

                <Form.Control
                  type="text"
                  value={inputValue}
                  onKeyDown={handleManageKeywords}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="tw-w-full tw-h-11 tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300"
                />

                <Form.Control.Feedback
                  type="invalid"
                  className="d-block font-poppins"
                >
                  {errors.Keywords?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {watch("Keywords").length > 0 && (
              <React.Fragment>
                <Col lg="12">
                  <div className="tw-h-[0px] tw-border tw-border-solid tw-border-gray-200" />
                </Col>

                <Col lg="12">
                  <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-mb-3">
                    {t("MacBotUserInputForm/key1")}
                  </div>
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-gap-2">
                    {watch("Keywords").map((keyword, index) => (
                      <div
                        key={index}
                        className="tw-h-9 tw-pl-[17px] tw-pr-[9px] tw-py-1.5 tw-bg-white tw-rounded-[99px] tw-shadow tw-border tw-border-gray-300 tw-justify-center tw-items-baseline tw-gap-1 tw-flex"
                      >
                        <div className="tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                          {keyword}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setValue(
                              "Keywords",
                              getValues("Keywords").filter(
                                (dr) => dr !== keyword
                              )
                            );
                          }}
                          className="tw-bg-transparent tw-border-none tw-p-0"
                        >
                          <BiX
                            size={18}
                            className="tw-text-gray-800 tw-text-xl"
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </Col>
              </React.Fragment>
            )}
            <Col xs={12}>
              <hr />
              <Form.Group>
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div className="tw-text-gray-400 tw-text-base tw-font-semibold tw-font-['Lexend'] tw-leading-normal">
                    {t("MacBotUserInputForm/l-04")}
                  </div>

                  <div className="tw-flex tw-gap-4 tw-items-center">
                    <Form.Switch {...register("isCaseSensitive")} />
                  </div>
                </div>
              </Form.Group>
            </Col>
            <HintBlock texts={["MacBotUserInputForm/l-05"]} />
          </Row>
        </div>

        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {itemData && (
              <DeleteButton
                deleteHandler={deleteHandler}
                onKeyDown={deleteHandler}
                tabIndex={0}
              />
            )}

            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <Button
              type="submit"
              className="tw-h-11 tw-px-5 tw-py-3 tw-bg-primary tw-rounded-[99px] tw-justify-center tw-items-center tw-inline-flex"
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                t("MacBotResponseForm/btn-02")
              )}
            </Button>
          </div>
        </div>
        <NoteForm
          noteFormMethods={{ register, watch, errors, fields, setValue }}
        />
      </ScenarioSide>
    </React.Fragment>
  );
};
